import { useEffect } from 'react';
import { Link, useNavigate, useParams, useRouteError, useSearchParams } from 'react-router';
import { LogIn, RefreshCw, ServerCrash, ShieldAlert } from 'lucide-react';
import { Button, Card, Spinner } from '@vwt/ui';
import { api } from '../../lib/api.js';
import { useTranslation } from '../../lib/i18n.js';
import { loginUrl } from '../../lib/session.js';

export function AuthErrorPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const reason = params.get('reason');
  const message = reason === 'state' ? t('auth.errorState') : reason === 'suspended' ? t('auth.errorSuspended') : t('auth.errorGeneric');
  return (
    <CenteredCard>
      <ShieldAlert className="size-8 text-warning" />
      <h1 className="text-base font-semibold">{t('auth.errorTitle')}</h1>
      <p className="text-sm text-fg-muted">{message}</p>
      <Button variant="primary" asChild>
        <a href={loginUrl('/my-work')}>{t('auth.tryAgain')}</a>
      </Button>
    </CenteredCard>
  );
}

/** The API could not be reached after retries (not an authentication failure). */
export function ServiceErrorPage() {
  const { t } = useTranslation();
  return (
    <CenteredCard>
      <ServerCrash className="size-8 text-fg-subtle" />
      <h1 className="text-base font-semibold">{t('errors.title')}</h1>
      <p className="text-sm text-fg-muted">{t('errors.unavailable')}</p>
      <Button variant="primary" onClick={() => window.location.reload()}>
        <RefreshCw /> {t('common.reload')}
      </Button>
    </CenteredCard>
  );
}

/** Route-level error boundary: a localized recovery page instead of the router's developer screen. */
export function RouteErrorPage() {
  const { t } = useTranslation();
  const error = useRouteError();
  useEffect(() => {
    console.error('route.error', error);
  }, [error]);
  return (
    <CenteredCard>
      <ServerCrash className="size-8 text-fg-subtle" />
      <h1 className="text-base font-semibold">{t('errors.title')}</h1>
      <p className="text-sm text-fg-muted">{t('errors.generic', { correlationId: new Date().toISOString() })}</p>
      <div className="flex gap-2">
        <Button variant="secondary" asChild>
          <a href="/my-work">{t('nav.myWork')}</a>
        </Button>
        <Button variant="primary" onClick={() => window.location.reload()}>
          <RefreshCw /> {t('common.reload')}
        </Button>
      </div>
    </CenteredCard>
  );
}

export function SignedOutPage() {
  const { t } = useTranslation();
  return (
    <CenteredCard>
      <img src="/logo.png" alt={t('app.name')} className="h-14 w-auto" />
      <Button variant="primary" asChild>
        <a href={loginUrl('/my-work')}>
          <LogIn /> {t('auth.signIn')}
        </a>
      </Button>
    </CenteredCard>
  );
}

export function SignInRedirect() {
  const { t } = useTranslation();
  useEffect(() => {
    window.location.assign(loginUrl());
  }, []);
  return (
    <CenteredCard>
      <Spinner />
      <p className="text-sm text-fg-muted">{t('auth.signingIn')}</p>
    </CenteredCard>
  );
}

/** /go/VAM-12 resolves display keys and imported aliases through the permission-filtered lookup. */
export function KeyRedirect() {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  useEffect(() => {
    void api
      .call('lookupKey', { query: { key: key! } })
      .then((r) => navigate(r!.data.kind === 'WORK_ITEM' ? `/work/${r!.data.key}` : `/requests/${r!.data.key}`, { replace: true }))
      .catch(() => navigate('/not-found', { replace: true }));
  }, [key, navigate]);
  return (
    <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
      <Spinner /> {t('common.loading')}
    </div>
  );
}

export function NotFoundPage() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-3 p-12 text-center">
      <p className="text-sm text-fg-muted">{t('errors.notFound')}</p>
      <Link to="/my-work" className="text-sm text-accent hover:underline">
        {t('nav.myWork')}
      </Link>
    </div>
  );
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-bg p-6">
      <Card className="flex w-full max-w-sm flex-col items-center gap-4 p-8 text-center">{children}</Card>
    </div>
  );
}
